export interface RaionItem {
    type: "raion";
    raionId: string;
    raionName?: string;
    roads: RaionRoadGroup[];
}

export interface RoadItem {
    type: "road";
    roadId: string;
    roadName: string;
    paths: Path[];
}

export interface Path {
    PathID: number;
    RoadId: string;
    RoadName?: string;
    Name?: string;
}

export interface RaionRoadGroup {
    roadId: string;
    roadName: string;
    paths: Path[];
}

export interface RaionInfo {
    raionId: string;
    raionName?: string;
    roads: RaionRoadGroup[];
}

export interface RoadRaionSegment {
    PathID: number;
    RaionId: string;
    RaionName: string;
}

// 1) Map<PathID, RoadRaionSegment[]>
export function groupSegmentsByPath(
    segs: RoadRaionSegment[]
): Map<number, RoadRaionSegment[]> {
    return segs.reduce((m, s) => {
        const arr = m.get(s.PathID) ?? [];
        arr.push(s);
        m.set(s.PathID, arr);
        return m;
    }, new Map<number, RoadRaionSegment[]>());
}

// 2) Собрать группы только по тем путям, у которых есть сегмент (то есть принадлежат реальному району)
export function buildRaionItems(
    paths: Path[],
    segMap: Map<number, RoadRaionSegment[]>
): RaionItem[] {
    const map = new Map<string, { raionName?: string; roads: Map<string, RaionRoadGroup> }>();

    for (const p of paths) {
        const segs = segMap.get(p.PathID) ?? [];
        for (const seg of segs) {
            if (!map.has(seg.RaionId)) {
                map.set(seg.RaionId, { raionName: seg.RaionName, roads: new Map() });
            }
            const roads = map.get(seg.RaionId)!.roads;
            if (!roads.has(p.RoadId)) {
                roads.set(p.RoadId, { roadId: p.RoadId, roadName: p.RoadName || "", paths: [] });
            }
            const grp = roads.get(p.RoadId)!;
            if (!grp.paths.some(x => x.PathID === p.PathID)) {
                grp.paths.push(p);
            }
        }
    }

    return Array.from(map.entries()).map(([raionId, { raionName, roads }]) => ({
        type: "raion" as const,
        raionId,
        raionName,
        roads: Array.from(roads.values())
            .map(g => ({ ...g, paths: g.paths.filter(p => p.Name != null) }))
            .sort((a, b) => a.roadName.localeCompare(b.roadName)),
    }));
}

// 3) Плоский список дорог, у которых нет ни одного сегмента (т.е. «без района»)
export function buildRoadItems(
    paths: Path[],
    segMap: Map<number, RoadRaionSegment[]>
  ): RoadItem[] {
    const map = new Map<string, RoadItem>();
  
    for (const p of paths) {
      // только пути без сегментов
      if ((segMap.get(p.PathID) ?? []).length === 0) {
        const key = p.RoadId;
        if (!map.has(key)) {
          map.set(key, {
            type:     "road",
            roadId:   p.RoadId,
            roadName: p.RoadName || "",
            paths:    [],
          });
        }
        map.get(key)!.paths.push(p);
      }
    }
  
    return Array.from(map.values());
  }

  export function buildRoadItemsAll(paths: Path[]): RoadItem[] {
  const byRoad = new Map<string, RoadItem>();
  for (const p of paths) {
    if (!byRoad.has(p.RoadId)) {
      byRoad.set(p.RoadId, { type: "road", roadId: p.RoadId, roadName: p.RoadName || "", paths: [] });
    }
    if (p.Name != null) byRoad.get(p.RoadId)!.paths.push(p);
  }
  return Array.from(byRoad.values()).sort((a, b) => a.roadName.localeCompare(b.roadName, "ru"));
}

// 4) contiguityFactor остаётся прежней (уже был в utils)
export function contiguityFactor(text: string, words: string[]): number {
    const positions = words.map(w => {
        const idxs: number[] = [];
        let pos = 0;
        while (true) {
            const i = text.indexOf(w, pos);
            if (i < 0) break;
            idxs.push(i);
            pos = i + 1;
        }
        return idxs;
    });
    if (positions.some(p => p.length === 0)) return 0;
    let best = Infinity;
    for (const start of positions[0]!) {
        let prev = start, last = start, ok = true;
        for (let i = 1; i < words.length; i++) {
            const next = positions[i]!.find(x => x > prev);
            if (!next) { ok = false; break; }
            prev = last = next;
        }
        if (ok) best = Math.min(best, last - start + words[words.length - 1].length);
    }
    return best === Infinity ? 0 : Math.min(1, words.length / best);
}
